import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Card } from "@/components/ui/Card";
import { SettingsSaveRow } from "@/pages/stammdaten/settings/SettingsSaveRow";
import {
  errorMessage,
  type DepositMode,
  type SettingsSectionProps,
  type TenantSettings,
} from "@/pages/stammdaten/settings/tenantSettingsTypes";

export function DepositSection({ settings, canWrite, onSaved }: SettingsSectionProps) {
  const [mode, setMode] = useState<DepositMode>(settings.deposit.mode);
  const [percent, setPercent] = useState(settings.deposit.percent);
  const [fixed, setFixed] = useState(settings.deposit.fixed_amount ?? "");
  const [deadlineDays, setDeadlineDays] = useState(
    String(settings.deposit.cancellation_deadline_days ?? 14),
  );
  const [deadlineTime, setDeadlineTime] = useState(
    settings.deposit.cancellation_deadline_time || "12:00",
  );
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMode(settings.deposit.mode);
    setPercent(settings.deposit.percent);
    setFixed(settings.deposit.fixed_amount ?? "");
    setDeadlineDays(String(settings.deposit.cancellation_deadline_days ?? 14));
    setDeadlineTime(settings.deposit.cancellation_deadline_time || "12:00");
  }, [settings]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const body: {
        mode: DepositMode;
        percent?: string;
        fixed_amount?: string;
        cancellation_deadline_days: number;
        cancellation_deadline_time: string;
      } = {
        mode,
        cancellation_deadline_days: Number.parseInt(deadlineDays.trim() || "14", 10),
        cancellation_deadline_time: deadlineTime || "12:00",
      };
      if (mode === "percent") {
        body.percent = percent.trim() || "30";
      } else if (mode === "fixed") {
        body.fixed_amount = fixed.trim() || "0";
      }
      await api.patch<TenantSettings>("/api/v1/settings/deposit", body);
      setSuccess(true);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, "Anzahlungseinstellungen konnten nicht gespeichert werden"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Anzahlung"
      description="Standard für neue Anzahlungsrechnungen. Die Anzahlung kann ein Prozentsatz, ein fester Reservierungsbetrag oder der Preis der ersten Nacht sein."
      bodyClassName="space-y-4"
    >
      <form onSubmit={(e) => void onSave(e)} className="space-y-4">
        <fieldset className="space-y-3" disabled={!canWrite}>
          <legend className="text-sm font-medium text-label">Anzahlungssystem</legend>
          <label className="flex cursor-pointer gap-3 rounded-lg border border-separator p-3 has-[:checked]:border-accent has-[:checked]:bg-accent/5">
            <input
              type="radio"
              name="deposit_mode"
              value="percent"
              checked={mode === "percent"}
              onChange={() => setMode("percent")}
              className="mt-1"
            />
            <span className="space-y-1">
              <span className="block text-sm font-medium text-label">Prozentualer Anzahlungsbetrag</span>
              <span className="block text-sm text-label-secondary">
                z. B. 30 % bei einer Buchung über 200 € → 60 € Anzahlung
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer gap-3 rounded-lg border border-separator p-3 has-[:checked]:border-accent has-[:checked]:bg-accent/5">
            <input
              type="radio"
              name="deposit_mode"
              value="fixed"
              checked={mode === "fixed"}
              onChange={() => setMode("fixed")}
              className="mt-1"
            />
            <span className="space-y-1">
              <span className="block text-sm font-medium text-label">Fester Anzahlungsbetrag</span>
              <span className="block text-sm text-label-secondary">
                Gleicher Reservierungsbetrag unabhängig von der Buchungssumme (höchstens die Buchungssumme)
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer gap-3 rounded-lg border border-separator p-3 has-[:checked]:border-accent has-[:checked]:bg-accent/5">
            <input
              type="radio"
              name="deposit_mode"
              value="first_night"
              checked={mode === "first_night"}
              onChange={() => setMode("first_night")}
              className="mt-1"
            />
            <span className="space-y-1">
              <span className="block text-sm font-medium text-label">Erste Nacht mit Gutschrift bei rechtzeitiger Absage</span>
              <span className="block text-sm text-label-secondary">
                Die Anzahlung ist der Preis der Anreisenacht. Bis zum eingestellten Zeitpunkt wird sie bei einer Absage gutgeschrieben, danach einbehalten.
              </span>
            </span>
          </label>
        </fieldset>
        {mode === "percent" ? (
          <label className="flex max-w-xs flex-col gap-1 text-sm">
            Prozentsatz
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              required
              className="eh-input"
              value={percent}
              onChange={(e) => setPercent(e.target.value)}
              disabled={!canWrite}
            />
          </label>
        ) : null}
        {mode === "fixed" ? (
          <label className="flex max-w-xs flex-col gap-1 text-sm">
            Fester Betrag (EUR)
            <input
              type="number"
              min="0"
              step="0.01"
              required
              className="eh-input"
              value={fixed}
              onChange={(e) => setFixed(e.target.value)}
              disabled={!canWrite}
            />
          </label>
        ) : null}
        {mode === "first_night" ? (
          <div className="grid max-w-lg gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              Tage vor der Anreise
              <input
                type="number"
                min="0"
                max="365"
                step="1"
                required
                className="eh-input"
                value={deadlineDays}
                onChange={(e) => setDeadlineDays(e.target.value)}
                disabled={!canWrite}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Uhrzeit
              <input
                type="time"
                required
                className="eh-input"
                value={deadlineTime}
                onChange={(e) => setDeadlineTime(e.target.value)}
                disabled={!canWrite}
              />
            </label>
            <p className="text-xs leading-relaxed text-label-tertiary sm:col-span-2">
              Beispiel: 14 Tage und 12:00 Uhr bedeutet, eine Absage bis 14 Tage vor der Anreise um 12:00 Uhr
              (Europe/Berlin) schreibt die Anzahlung gut. Die Frist steht auf der Anzahlungsrechnung.
            </p>
          </div>
        ) : null}
        <SettingsSaveRow
          canWrite={canWrite}
          saving={saving}
          success={success}
          successText="Anzahlungseinstellungen gespeichert."
          error={error}
          label="Anzahlung speichern"
        />
      </form>
    </Card>
  );
}
